import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import AdminLayout from '../AdminLayout';
import competitionService from '../../services/competitionService';
import startupService from '../../services/startupService';

export default function CompetitionForm() {
    const navigate = useNavigate();
    const { compId } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [startups, setStartups] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        lastDateToApply: '',
        resultDeclarationDate: '',
        startupId: '',
        status: 'Ongoing',
        competitionLink: '', // ✅ NEW
    });

    useEffect(() => {
        fetchStartups();
        if (compId) fetchCompetition();
    }, [compId]);

    const fetchStartups = async () => {
        try {
            const data = await startupService.getStartups();
            setStartups(data);
        } catch (err) {
            console.error('Error fetching startups:', err);
        }
    };

    const fetchCompetition = async () => {
        try {
            const data = await competitionService.getCompetitionById(compId);
            setFormData(data);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            if (!formData.title.trim()) {
                setError('Title is required');
                return;
            }
            if (!formData.startupId) {
                setError('Please select a startup');
                return;
            }

            setLoading(true);

            if (compId) {
                await competitionService.updateCompetition(compId, formData);
            } else {
                await competitionService.createCompetition(formData);
            }

            navigate('/competitions');
        } catch (err) {
            console.error('Error:', err);
            setError(err.message || 'Failed to save competition');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminLayout title={compId ? 'Edit Competition' : 'Create Competition'}>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
                <div className="max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <button
                            onClick={() => navigate('/competitions')}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                        >
                            <BackIcon /> Back
                        </button>
                        <h1 className="text-4xl font-bold text-gray-900">
                            {compId ? '✏️ Edit Competition' : '🏆 Create Competition'}
                        </h1>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between">
                            <span>{error}</span>
                            <button onClick={() => setError('')} className="font-bold">×</button>
                        </div>
                    )}

                    {/* Form */}
                    <div className="bg-white rounded-xl shadow-xl p-8">
                        <div className="space-y-6">
                            {/* Startup Selection */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Select Startup *
                                </label>
                                <select
                                    name="startupId"
                                    value={formData.startupId}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="">-- Choose a startup --</option>
                                    {startups.map(startup => (
                                        <option key={startup.id} value={startup.id}>
                                            {startup.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Title */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Competition Titleeeee *
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="e.g., Startup Pitch Competition 2024"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Competition details..."
                                    rows="4"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                ></textarea>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Competition Webpage Link
                                </label>
                                <input
                                    type="url"
                                    name="competitionLink"
                                    value={formData.competitionLink}
                                    onChange={handleChange}
                                    placeholder="https://example.com/competition-details"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-black-700 mb-2">
                                        Last Date to Apply
                                    </label>
                                    <input
                                        type="date"
                                        name="lastDateToApply"
                                        value={formData.lastDateToApply}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-black-700 mb-2">
                                        Result Declaration Date
                                    </label>
                                    <input
                                        type="date"
                                        name="resultDeclarationDate"
                                        value={formData.resultDeclarationDate}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Status
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="Ongoing">Ongoing</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Upcoming">Upcoming</option>
                                </select>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-4 justify-end mt-8">
                            <button
                                onClick={() => navigate('/competitions')}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={loading}
                                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50"
                            >
                                {loading ? '⏳ Saving...' : '✓ Save'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
